import { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import { RS1 } from "./instruments/RS1/RS1";
import { RS808 } from "./instruments/RS808/RS808";
import { RSKalimba } from "./instruments/RSKalimba/RSKalimba";
import { RSMbira } from "./instruments/RSMbira/RSMbira";

interface Instrument {
  id: string;
  name: string;
  description: string;
  status: "ready" | "coming_soon";
  component?: React.ComponentType;
}

const INSTRUMENTS: Instrument[] = [
  { id: "rs1", name: "RS-1", description: "Synthesizer", status: "ready", component: RS1 },
  { id: "rs808", name: "RS-808", description: "Drum Machine", status: "ready", component: RS808 },
  { id: "rs-kalimba", name: "RS-Kalimba", description: "Thumb Piano", status: "ready", component: RSKalimba },
  { id: "rs-mbira", name: "RS-Mbira", description: "Zimbabwean Mbira", status: "ready", component: RSMbira },
];

function getInstrumentFromHash(): string {
  const hash = window.location.hash.slice(1);
  const valid = INSTRUMENTS.find((i) => i.id === hash);
  return valid ? hash : "rs1";
}

const HELP_CONTENT: Record<string, { keys: string[][]; tips: string }> = {
  rs1: {
    keys: [
      ["White keys", "A S D F G H J K L ; '"],
      ["Black keys", "W E T Y U O P"],
      ["Octave down/up", "Z / X"],
      ["Knobs", "Click + drag up/down"],
      ["MIDI", "Any controller (Chrome/Edge)"],
    ],
    tips: "Use presets to explore sounds. Adjust OSC, Filter, and Envelope for custom tones.",
  },
  rs808: {
    keys: [
      ["Row 1", "1 2 3 4"],
      ["Row 2", "Q W E R"],
      ["Row 3", "A S D F"],
      ["Row 4", "Z X C V"],
      ["Play/Stop", "Space"],
      ["MIDI", "GM drums (Chrome/Edge)"],
    ],
    tips: "Tap a pad to select it for sequencer. Click steps to toggle on/off.",
  },
  "rs-kalimba": {
    keys: [
      ["Top row", "Q W E R T Y U P"],
      ["Bottom row", "A S D F G H J K L"],
      ["Center (C4)", "Space"],
      ["Knobs", "Click + drag up/down"],
      ["MIDI", "Closest note (Chrome/Edge)"],
    ],
    tips: "Tines arranged in V pattern - lower notes outside, higher in center.",
  },
  "rs-mbira": {
    keys: [
      ["Left (bass)", "Q W E R T Y U"],
      ["Right (mid)", "H J K L ; ' [ ]"],
      ["Upper (high)", "1 2 3 4 5 6"],
      ["Knobs", "Click + drag up/down"],
      ["MIDI", "Closest note (Chrome/Edge)"],
    ],
    tips: "Traditional Zimbabwean mbira dzavadzimu. Three manuals played with thumbs and index finger.",
  },
};

export function MusicStudioPage() {
  const [activeInstrument, setActiveInstrument] = useState<string>(getInstrumentFromHash);
  const [showHelp, setShowHelp] = useState(false);

  const selectInstrument = useCallback((id: string) => {
    setActiveInstrument(id);
    window.location.hash = id;
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      setActiveInstrument(getInstrumentFromHash());
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const activeInst = INSTRUMENTS.find((i) => i.id === activeInstrument);

  return (
    <>
      <Helmet>
        <title>MokaStudio | mokachika</title>
        <meta
          name="description"
          content="Browser-based music studio with synthesizer and drum machine. Create music with RS-1 and RS-808."
        />
      </Helmet>

      <div className="min-h-screen bg-[#0a0a0a] flex flex-col md:flex-row font-sans relative">
        {/* Global Help Button */}
        <button
          onClick={() => setShowHelp(!showHelp)}
          className={`fixed top-3 right-3 md:top-4 md:right-4 w-8 h-8 rounded-full text-sm font-bold transition-colors z-50 ${
            showHelp ? "bg-[#00ff88] text-black" : "bg-[#222] text-[#666] hover:text-[#00ff88] border border-[#333]"
          }`}
        >
          ?
        </button>

        {/* Help Panel */}
        {showHelp && HELP_CONTENT[activeInstrument] && (
          <div className="fixed top-14 right-3 md:right-4 w-72 bg-[#1a1a1a] rounded-xl p-4 text-[11px] text-[#888] border border-[#333] z-50 shadow-xl">
            <div className="font-semibold text-[#00ff88] mb-3">
              {activeInst?.name} - Keyboard Controls
            </div>
            <div className="space-y-1.5">
              {HELP_CONTENT[activeInstrument].keys.map(([label, keys]) => (
                <div key={label} className="flex justify-between">
                  <span className="text-[#555]">{label}</span>
                  <span className="text-[#aaa] font-mono">{keys}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-[#333] text-[#666]">
              {HELP_CONTENT[activeInstrument].tips}
            </div>
          </div>
        )}
        {/* Sidebar - Desktop */}
        <aside className="hidden md:flex w-56 bg-[#111] border-r border-[#222] flex-col">
          {/* Header */}
          <div className="p-4 border-b border-[#222]">
            <a href="/" className="text-[#555] text-xs hover:text-[#888] transition-colors">
              ← mokachika
            </a>
            <h1 className="text-lg text-white mt-2 font-serif tracking-wide text-center">MokaStudio</h1>
          </div>

          {/* Instrument List */}
          <nav className="flex-1 p-3 space-y-1">
            <div className="text-xs uppercase tracking-wider text-[#555] px-3 py-2">
              Instruments
            </div>
            <div
              className="text-[9px] px-3 pb-2 cursor-help text-[#666]"
              title="MIDI works natively in Chrome/Edge. Firefox users need the Jazz-MIDI extension from https://jazz-soft.net"
            >
              <span className="text-[#888]">MIDI Supported</span> · Chrome/Edge
            </div>
            {INSTRUMENTS.map((inst) => (
              <button
                key={inst.id}
                onClick={() => selectInstrument(inst.id)}
                className={`w-full text-left p-3 rounded-lg transition-all ${
                  activeInstrument === inst.id
                    ? "bg-[#1a1a1a] border border-[#333]"
                    : "hover:bg-[#1a1a1a] border border-transparent"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`font-medium ${
                      activeInstrument === inst.id ? "text-[#00ff88]" : "text-white"
                    }`}
                  >
                    {inst.name}
                  </span>
                  {inst.status === "coming_soon" && (
                    <span className="text-[9px] uppercase tracking-wide text-[#555] bg-[#222] px-1.5 py-0.5 rounded">
                      Soon
                    </span>
                  )}
                </div>
                <div className="text-sm text-[#666] mt-0.5">{inst.description}</div>
              </button>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-[#222] text-xs text-[#555]">
            Press ? for controls
          </div>
        </aside>

        {/* Mobile Header */}
        <header className="md:hidden flex items-center justify-between p-3 border-b border-[#222] bg-[#111]">
          <a href="/" className="text-[#555] text-xs">← back</a>
          <h1 className="text-sm text-white font-serif tracking-wide">MokaStudio</h1>
          <div className="w-8" /> {/* Spacer for centering */}
        </header>

        {/* Workspace */}
        <main className="flex-1 flex items-center justify-center p-4 md:p-6 overflow-auto pb-20 md:pb-6">
          {activeInst?.status === "coming_soon" ? (
            <div className="text-center">
              <div className="text-6xl mb-4">
                {activeInst.id === "rs1" ? "🎹" : "🥁"}
              </div>
              <h2 className="text-2xl font-semibold text-white mb-2">{activeInst.name}</h2>
              <p className="text-[#666] mb-6">{activeInst.description}</p>
              <div className="inline-flex items-center gap-2 text-sm text-[#555] bg-[#1a1a1a] px-4 py-2 rounded-full border border-[#333]">
                <span className="w-2 h-2 rounded-full bg-[#f97316] animate-pulse" />
                Coming soon
              </div>
            </div>
          ) : activeInst?.component ? (
            <div className="w-full max-w-4xl">
              <activeInst.component />
            </div>
          ) : null}
        </main>

        {/* Mobile Bottom Tabs */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[#111] border-t border-[#222] flex">
          {INSTRUMENTS.map((inst) => (
            <button
              key={inst.id}
              onClick={() => selectInstrument(inst.id)}
              className={`flex-1 py-3 px-2 flex flex-col items-center gap-1 transition-colors ${
                activeInstrument === inst.id ? "text-[#00ff88]" : "text-[#666]"
              }`}
            >
              <span className="text-xs font-medium">{inst.name}</span>
              <span className="text-[9px] text-[#555]">{inst.description}</span>
            </button>
          ))}
        </nav>
      </div>
    </>
  );
}
